import { ThreeDotVerticalIcon } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { useMemo, useState } from 'react'

import { BottomSheet, Dropdown } from '@/components'
import { UpdateIssueForm } from '@/forms/ReportIssue/UpdateIssueForm'
import { useToggle } from '@/hooks'
import { HomeIssue, HomeIssueType } from '@/types/HomeIssue'

import { HomeQualityCheckSummaryMedia } from '../Summary/Media'
import { formattedDatetime } from './HomeIssue.data'
import { ResolveHomeIssue } from './ResolveHomeIssue'

type Props = {
  homeIssue: HomeIssue
  refetchOnSuccess: () => void
}

type SummaryOption = {
  className: string
  label: string
}

export const HomeMobileIssueCard = ({ homeIssue, refetchOnSuccess }: Props) => {
  const { type, homeLocation, createdAt, homeInventoryItem, id } = homeIssue
  const createdDate = formattedDatetime(createdAt)

  const options = useMemo(() => {
    let options: SummaryOption[] = []

    options.push({ label: 'Resolve', className: 'text-action-primary' })
    options.push({ label: 'Edit', className: 'text-action-input' })
    if (homeIssue.attachments && homeIssue.attachments.length > 0) {
      options.push({ label: 'View Media', className: 'text-action-input' })
    }
    return options
  }, [homeIssue])

  const [isOpen, setIsOpen] = useState(false)
  const { isShown: resolveHomeIssueModalShown, toggle: resolveHomeIssueModalToggle } = useToggle()
  const { isShown: editHomeIssueModalShown, toggle: editHomeIssueModalToggle } = useToggle()
  const { isShown: mediaModalShown, toggle: mediaModalToggle } = useToggle()

  const handleHomeIssueAction = (type: string) => {
    if (id) {
      switch (type) {
        case 'Edit': {
          setIsOpen(false)
          editHomeIssueModalToggle()
          break
        }
        case 'Resolve': {
          setIsOpen(false)
          resolveHomeIssueModalToggle()
          break
        }
        case 'View Media': {
          setIsOpen(false)
          mediaModalToggle()
          break
        }
        default: {
          return
        }
      }
    }
  }

  return (
    <BoxStyled role="presentation">
      <div className="home-issue-information grid grid-cols-4">
        <div className="bordered-column">
          <h6>Location</h6>
          <p>{homeLocation}</p>
        </div>
        <div className="bordered-column">
          <h6>Type</h6>
          <p className={`${getHomeIssueTypeClassName(type)} `}>{type}</p>
        </div>
        <div className="bordered-column">
          <h6>Inventory Item</h6>
          <p>{homeInventoryItem?.name || 'N/A'}</p>
        </div>
        <div className="pl-2">
          <div className="mr-4">
            <h6>Date</h6>
            <p>{createdDate}</p>
          </div>
          <div className="absolute right-2 ml-auto flex items-center justify-center pr-2">
            <Dropdown
              className="my-2 md:my-0 relative"
              dropdownWrapperClassName="-top-18 right-8"
              expanded={isOpen}
              onHideDropdown={() => setIsOpen(false)}
              renderToggle={() => {
                return <ThreeDotVerticalIcon className="text-primary" height={16} />
              }}
              setExpanded={setIsOpen}
            >
              <div className="right-0 top-0 flex flex-col py-2">
                {options.map((o, i) => {
                  return (
                    <button
                      className={`mb-1 w-full cursor-pointer px-6 py-3 leading-none hover:bg-action-secondary ${o.className}`}
                      key={i}
                      onClick={() => handleHomeIssueAction(o.label)}
                    >
                      {o.label}
                    </button>
                  )
                })}
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
      <BottomSheet isVisible={resolveHomeIssueModalShown} onClose={resolveHomeIssueModalToggle}>
        <ResolveHomeIssue homeIssueId={id} refetchDetails={refetchOnSuccess} toggle={resolveHomeIssueModalToggle} />
      </BottomSheet>
      <BottomSheet header="Edit Home Issue" isVisible={editHomeIssueModalShown} onClose={editHomeIssueModalToggle}>
        <UpdateIssueForm homeIssue={homeIssue} refetchOnSuccess={refetchOnSuccess} toggle={editHomeIssueModalToggle} />
      </BottomSheet>
      {mediaModalShown && homeIssue.attachments && homeIssue.attachments.length > 0 ? (
        <BottomSheet header="Media" isVisible={mediaModalShown} onClose={mediaModalToggle}>
          <HomeQualityCheckSummaryMedia attachments={homeIssue.attachments} />
        </BottomSheet>
      ) : null}
    </BoxStyled>
  )
}

const getHomeIssueTypeClassName = (value: HomeIssueType | undefined): string => {
  switch (value) {
    case HomeIssueType.ACCESS:
      return 'text-semantic-success'
    case HomeIssueType.DAMAGE:
      return 'text-semantic-error'
    case HomeIssueType.REPLACEMENT_NEEDED:
      return 'text-semantic-info'
    case HomeIssueType.WIFI:
      return 'text-semantic-warning'
    default:
      return 'text-semantic-black'
  }
}

const BoxStyled = styled.div`
  position: relative;
  padding: 0.75rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 1rem;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.12);

  .home-issue-information {
    div {
      display: flex;
      flex-direction: column;
    }

    h6 {
      color: #787573;
      font-size: var(--text-p-xs);
    }
    p {
      font-size: var(--text-p-xs);
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    p:hover {
      overflow: visible;
      white-space: normal;
      height: auto;
    }
  }

  .bordered-column {
    border-right-width: 1px;
    padding-left: 0.5rem;
    border-color: #e7e6e4;
  }
`
